import { Alert, AlertProps, CircularProgress, Typography } from '@mui/joy';
import { CheckCircle, Warning, XCircle } from '@phosphor-icons/react';
import { CSSProperties } from 'react';

import { NotificationCardType } from 'core/enums';
import { renderHtmlMessage } from 'core/utils';
import { theme } from 'theme';
import { Color, Level, Size } from 'theme/enums';

interface IProps extends AlertProps {
  title?: string;
  message: string | JSX.Element;
  type: NotificationCardType;
  verticalAlign?: boolean;
  size?: Size;
  sx?: CSSProperties;
}

const getIconStyle = (verticalAlign: boolean, type: NotificationCardType = null) => ({
  width: verticalAlign ? 40 : 24,
  height: verticalAlign ? 40 : 24,
  fill: type === NotificationCardType.WARNING ? theme.palette.warning[400] : 'inherit',
});

const getIconSxProps = (verticalAlign: boolean) => ({
  ['--_root-size' as string]: verticalAlign ? '32px' : '16px',
});

const getAlertSxProps = (verticalAlign: boolean) => ({
  height: '100%',
  display: 'flex',
  flexDirection: verticalAlign ? 'column' : 'row',
  justifyContent: 'center',
  gap: 1,
  ...(!verticalAlign && {
    alignItems: 'flex-start',

    '& .MuiAlert-startDecorator': {
      marginTop: '2px',
    },
  }),
});

const colorMap = {
  [NotificationCardType.SUCCESS]: Color.SUCCESS,
  [NotificationCardType.ERROR]: Color.DANGER,
  [NotificationCardType.LOADING]: Color.PRIMARY,
  [NotificationCardType.WARNING]: Color.WARNING,
};

const getStartDecoratorMap = (verticalAlign: boolean) => ({
  [NotificationCardType.SUCCESS]: <CheckCircle style={getIconStyle(verticalAlign)} />,
  [NotificationCardType.ERROR]: <XCircle style={getIconStyle(verticalAlign)} />,
  [NotificationCardType.LOADING]: (
    <CircularProgress size={Size.SM} sx={getIconSxProps(verticalAlign)} />
  ),
  [NotificationCardType.WARNING]: (
    <Warning style={getIconStyle(verticalAlign, NotificationCardType.WARNING)} />
  ),
});

const NotificationCard = ({
  title,
  message,
  type,
  size = Size.LG,
  verticalAlign = true,
  sx,
  ...rest
}: IProps) => {
  return (
    <Alert
      startDecorator={getStartDecoratorMap(verticalAlign)[type]}
      color={colorMap[type]}
      size={size}
      {...rest}
      sx={{
        ...getAlertSxProps(verticalAlign),
        ...sx,
      }}
    >
      <>
        {title && (
          <Typography level={Level.H4} color={colorMap[type]}>
            {title}
          </Typography>
        )}

        <Typography
          level={Level.BODY_MD}
          color={type === NotificationCardType.WARNING ? null : colorMap[type]}
          sx={{ ...(type === NotificationCardType.WARNING && { color: 'inherit' }) }}
          component="div"
        >
          {renderHtmlMessage(message)}
        </Typography>
      </>
    </Alert>
  );
};

export default NotificationCard;
